"use client";

type SiteSightApi = { setConsent?: (granted: boolean) => void; __loaded?: boolean };
type SiteSightWindow = Window & { customySiteSight?: SiteSightApi; __customySiteSightConsent?: boolean };
type Runtime = { enabled: true; scriptUrl: string; revision: number };
let script: HTMLScriptElement | null = null;
let consentRevision = 0;

function revoke(host: SiteSightWindow) {
  host.__customySiteSightConsent = false;
  host.customySiteSight?.setConsent?.(false);
  script?.remove();
  script = null;
}

/** SiteSight's hosted SDK is fetched only after its dedicated opt-in. */
export async function updateSiteSightConsent(granted: boolean): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const requestRevision = ++consentRevision;
  const host = window as SiteSightWindow;
  host.__customySiteSightConsent = granted;
  if (!granted) {
    revoke(host);
    return;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch("/api/sitesight/config", { credentials: "same-origin", cache: "no-store", redirect: "error", signal: controller.signal });
    if (!response.ok) { if (requestRevision === consentRevision) revoke(host); return; }
    const value: unknown = await response.json();
    if (requestRevision !== consentRevision) return;
    if (!value || typeof value !== "object" || (value as Runtime).enabled !== true
      || typeof (value as Runtime).scriptUrl !== "string" || !Number.isSafeInteger((value as Runtime).revision)) {
      revoke(host);
      return;
    }
    const runtime = value as Runtime;
    if (!runtime.scriptUrl.startsWith("https://")) { revoke(host); return; }
    if (script && script.src === runtime.scriptUrl) {
      host.customySiteSight?.setConsent?.(true);
      return;
    }
    // Changing the Workspace binding changes the site key inside the hosted
    // SDK. Stop and discard the old recorder before loading the new handle.
    if (script) revoke(host);
    if (typeof host.customySiteSight?.setConsent !== "function" && host.customySiteSight?.__loaded === true) host.customySiteSight.__loaded = false;
    script = document.createElement("script");
    script.async = true;
    script.src = runtime.scriptUrl;
    document.head.append(script);
  } catch {
    // Fail closed when current consent or the Workspace binding cannot be revalidated.
    if (requestRevision === consentRevision) revoke(host);
  } finally {
    clearTimeout(timer);
  }
}
